package dev.lingvo.site.pages.app.projects.slug.screenshots

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.CaptureDto
import dev.lingvo.shared.ReviewCaptureRequest
import dev.lingvo.shared.CoverageRowDto
import dev.lingvo.shared.ScreenshotSpecs
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.screenshotCaptureImage
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.reviewScreenshotCapture
import dev.lingvo.site.api.restoreScreenshotCapture
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LFreshness
import dev.lingvo.site.ds.LKeyValueRow
import dev.lingvo.site.ds.LLinkButton
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionHeading
import dev.lingvo.site.ds.ScrimVars
import dev.lingvo.site.ds.LSheetEdge
import dev.lingvo.site.ds.prefersReducedMotion
import dev.lingvo.site.ds.sheetHidden
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.sheetSurface
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.yield
import kotlinx.coroutines.launch
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlin.coroutines.resume
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Aside
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement
import org.w3c.dom.events.Event
import org.w3c.dom.events.KeyboardEvent
import org.w3c.dom.url.URL
import kotlin.js.Date

/** Keeps the phone sheet inside the viewport and turns it into a 360-pixel detail column on desktop. */
val ShotCellDrawerStyle = CssStyle {
    base { Modifier.sheetSurface().styleModifier {
        property("position", "fixed"); property("inset-inline", "0"); property("bottom", "0")
        property("width", "100%"); property("height", "100dvh"); property("max-width", "100vw")
        property("overflow", "auto"); property("overscroll-behavior", "contain"); property("z-index", "900"); property("box-sizing", "border-box")
        property("padding", Space.LG.toString()); property("background", colorMode.toSitePalette().surface.toString())
        property("border", "1px solid ${colorMode.toSitePalette().borderStrong}")
    } }
    Breakpoint.MD { Modifier.styleModifier { property("height", "60dvh") } }
    Breakpoint.XL { Modifier.styleModifier {
        property("position", "sticky"); property("inset-inline", "auto"); property("top", "0")
        property("height", "auto"); property("max-height", "90dvh"); property("width", "360px"); property("z-index", "1")
    } }
}

/** Authenticated image bytes live only in an object URL owned by this open cell. */
@Composable
fun ShotCellDrawer(
    slug: String, params: ScreenshotsAreaState, row: CoverageRowDto, cell: ShotGridCell, locale: String,
    capture: CaptureDto?, canWrite: Boolean, onClose: () -> Unit, onUpload: () -> Unit,
    onRetake: () -> Unit, onDelete: () -> Unit,
    history: List<CaptureDto> = emptyList(), onCaptureChanged: (CaptureDto) -> Unit = {},
    image: suspend (String) -> org.w3c.files.Blob = { ApiClient.screenshotCaptureImage(slug, params.platform, it) },
) {
    val scope = rememberCoroutineScope()
    val identity = Session.requestIdentity
    val actions = remember(slug, params.platform, identity) { ScreenshotsActionRunner(slug, { scope.launch { it() } }) }
    var captureBusy by remember(capture?.id) { mutableStateOf(false) }
    var captureError by remember(capture?.id) { mutableStateOf<String?>(null) }
    var origin by remember { mutableStateOf<HTMLElement?>(null) }
    val close by rememberUpdatedState(onClose)
    var narrow by remember { mutableStateOf(window.innerWidth < 1280) }
    val reducedMotion = prefersReducedMotion()
    var entered by remember { mutableStateOf(!narrow || reducedMotion) }
    val latestCell by rememberUpdatedState("${row.slotId}:$locale")
    LaunchedEffect(Unit) {
        if (!entered) {
            document.getElementById("shot-cell-drawer")?.getBoundingClientRect()
            suspendCancellableCoroutine<Unit> { continuation ->
                val frame = window.requestAnimationFrame { if (continuation.isActive) continuation.resume(Unit) }
                continuation.invokeOnCancellation { window.cancelAnimationFrame(frame) }
            }
            entered = true
        }
    }
    var objectUrl by remember(capture?.id) { mutableStateOf<String?>(null) }
    var imageFailure by remember(capture?.id) { mutableStateOf<String?>(null) }
    val title = tf("screenshots.drawer.title", "{shot} · {language} · {device}", "shot" to row.title,
        "language" to localeInfo(locale).englishName, "device" to (cell.device?.displayName ?: t("app.projects.slug.screenshots.coverage_cell_drawer.all_devices", "All devices")))
    DisposableEffect(capture?.id) {
        onDispose { objectUrl?.let(URL::revokeObjectURL); objectUrl = null }
    }
    LaunchedEffect(capture?.id) {
        if (capture != null && !capture.blobMissing) try {
            val blob = image(capture.id)
            objectUrl = URL.createObjectURL(blob)
        } catch (cancelled: CancellationException) { throw cancelled }
        catch (failure: Throwable) { imageFailure = shotRequestFailure(failure) }
    }
    DisposableEffect(Unit) {
        origin = document.activeElement as? HTMLElement
        val keys: (Event) -> Unit = listener@{ event ->
            val keyboard = event as? KeyboardEvent ?: return@listener
            if (document.querySelector("[role=dialog][aria-modal=true]:not(#shot-cell-drawer):not([aria-hidden=true])") != null) return@listener
            if (keyboard.key == "Escape") { keyboard.preventDefault(); close() }
            if (narrow && keyboard.key == "Tab") {
                val nodes = document.getElementById("shot-cell-drawer")?.querySelectorAll("button:not(:disabled), a[href], input, select, textarea")
                val first = nodes?.item(0) as? HTMLElement
                val last = nodes?.item(nodes.length - 1) as? HTMLElement
                if (keyboard.shiftKey && (document.activeElement == first || document.activeElement?.id == "shot-cell-drawer")) { keyboard.preventDefault(); last?.focus() }
                else if (!keyboard.shiftKey && document.activeElement == last) { keyboard.preventDefault(); first?.focus() }
            }
        }
        val resize: (Event) -> Unit = {
            narrow = window.innerWidth < 1280
        }
        window.addEventListener("resize", resize)
        window.addEventListener("keydown", keys)
        onDispose {
            window.removeEventListener("resize", resize)
            window.removeEventListener("keydown", keys)
            val active = document.activeElement
            if (active == document.body || document.getElementById("shot-cell-drawer")?.contains(active) == true) {
                val buttons = document.querySelectorAll("button[data-shot-cell]")
                val cellButton = (0 until buttons.length).mapNotNull { buttons.item(it) as? HTMLElement }
                    .firstOrNull { it.getAttribute("data-shot-cell") == latestCell }
                (cellButton ?: origin)?.takeIf { document.contains(it) }?.focus()
            }
        }
    }
    LaunchedEffect(row.slotId, locale, entered) {
        if (!entered) return@LaunchedEffect
        yield()
        val drawer = document.getElementById("shot-cell-drawer") as? HTMLElement
        (document.activeElement as? HTMLElement)?.takeIf { drawer?.contains(it) != true }?.let { origin = it }
        drawer?.asDynamic()?.focus(js("({preventScroll:true})")) // i18n-ignore
    }
    if (narrow) Div(Modifier.styleModifier {
        property("position", "fixed"); property("inset", "0"); property("z-index", "899"); property("background", ScrimVars.Sheet.value().toString())
    }.toAttrs {
        attr("aria-hidden", "true"); onClick { onClose() }
        onWheel { it.preventDefault() }
        onTouchMove { it.preventDefault() }
    })
    Aside(ShotCellDrawerStyle.toModifier().then(if (narrow && !entered) Modifier.sheetHidden(LSheetEdge.BOTTOM) else Modifier).toAttrs {
        id("shot-cell-drawer"); attr("role", "dialog"); attr("aria-label", title); tabIndex(-1)
        attr("aria-modal", narrow.toString()); attr("data-shot-drawer", if (narrow) "sheet" else "panel")
    }) {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            LButton(t("app.projects.slug.screenshots.coverage_cell_drawer.close_capture", "Close capture"), onClose, variant = LButtonVariant.GHOST)
            LSectionHeading(title, coverageCellLabel(row.title, locale, cell, capture?.capturedAt))
            LBadge(when (cell.status) {
                ShotCellStatus.CURRENT -> t("app.projects.slug.screenshots.coverage_cell_drawer.current", "Current")
                ShotCellStatus.STALE -> t("app.projects.slug.screenshots.coverage_cell_drawer.stale", "Stale")
                ShotCellStatus.MISSING -> t("app.projects.slug.screenshots.coverage_cell_drawer.missing", "Missing")
                ShotCellStatus.NOT_REQUIRED -> t("app.projects.slug.screenshots.coverage_cell_drawer.not_required", "Not required")
                ShotCellStatus.REFUSED -> t("app.projects.slug.screenshots.coverage_cell_drawer.refused", "Refused")
            })
            if (capture == null || capture.blobMissing) LAlert(tf("screenshots.drawer.wanted", "Wanted: {size}, PNG or JPEG. Transparency is accepted in captures and flattened for the store.",
                "size" to (cell.device?.let { ScreenshotSpecs.describe(it.spec.captureRule) }.orEmpty())))
            objectUrl?.let { url -> Img(src = url, alt = title, attrs = Modifier.fillMaxWidth().toAttrs {}) }
            imageFailure?.let { LAlert(it) }
            if (capture != null) {
                LKeyValueRow(t("app.projects.slug.screenshots.coverage_cell_drawer.size", "Size"), "${capture.width} × ${capture.height}")
                LKeyValueRow(t("app.projects.slug.screenshots.coverage_cell_drawer.type", "Type"), if (capture.hasAlpha) tf("screenshots.drawer.alpha", "{format}, transparency", "format" to capture.format.name)
                    else tf("screenshots.drawer.opaque", "{format}, no transparency", "format" to capture.format.name))
                LKeyValueRow(t("app.projects.slug.screenshots.coverage_cell_drawer.captured", "Captured"), tf("screenshots.drawer.by", "{when} by {actor}",
                    "when" to capture.capturedAt, "actor" to capture.uploadedBy))
                LFreshness(Date(capture.capturedAt))
                capture.stale?.let { stale ->
                    stale.reasons.forEach { LMuted(staleReasonLabel(it)) }
                    stale.sample.take(5).forEach { sample ->
                        A(href = "/app/projects/${encodeURIComponent(slug)}/keys?search=${encodeURIComponent(sample.keyName)}", attrs = Modifier.monoFont().styleModifier { property("display", "inline-flex"); property("min-height", "40px"); property("align-items", "center"); property("overflow-wrap", "anywhere") }.toAttrs {}) { Text(sample.keyName) }
                    }
                    val more = stale.changedKeys + stale.addedKeys + stale.removedKeys - stale.sample.take(5).size
                    if (more > 0) LMuted(tf("screenshots.drawer.more", "and {count} more", "count" to more))
                }
            }
            CaptureReviewControls(capture, history, canWrite, captureBusy, captureError, { state ->
                val current = capture ?: return@CaptureReviewControls
                actions.run(ScreenshotsPrivilege.CAPTURE, { captureBusy = it }, {
                    ApiClient.reviewScreenshotCapture(slug, params.platform, current.id, ReviewCaptureRequest(state))
                }, { captureError = shotRequestFailure(it) }, { captureError = null; onCaptureChanged(it) })
            }, { id ->
                actions.run(ScreenshotsPrivilege.CAPTURE, { captureBusy = it }, {
                    ApiClient.restoreScreenshotCapture(slug, params.platform, id)
                }, { captureError = shotRequestFailure(it) }, { captureError = null; onCaptureChanged(it) })
            })
            if (canWrite && cell.device != null) {
                LButton(t("app.projects.slug.screenshots.coverage_cell_drawer.upload_a_replacement", "Upload a replacement"), onUpload, variant = LButtonVariant.SECONDARY)
                LButton(t("app.projects.slug.screenshots.coverage_cell_drawer.queue_a_retake", "Queue a retake"), onRetake, variant = LButtonVariant.SECONDARY)
            }
            LLinkButton(t("app.projects.slug.screenshots.coverage_cell_drawer.open_in_the_studio", "Open in the studio"), screenshotsPath(slug, ScreenshotsTab.DESIGN,
                params.copy(slot = row.slotId, locale = locale, device = cell.device)), variant = LButtonVariant.SECONDARY)
            if (canWrite && capture != null) LButton(t("app.projects.slug.screenshots.coverage_cell_drawer.delete_capture", "Delete capture"), onDelete, variant = LButtonVariant.DANGER)
        }
    }
}

private external fun encodeURIComponent(value: String): String
